import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}


// Calton Brown: expose AccessEaze beside the country/language selector in the mobile drawer.
(() => {
  const installAccessibilityTriggers = () => {
    document.querySelectorAll('.menu-drawer__utility-links').forEach((utility) => {
      if (utility.querySelector('.cb-accessibility-trigger-row')) return;

      utility.closest('.menu-drawer')?.querySelectorAll('.cb-accessibility-nav-item').forEach((item) => item.remove());

      const localization = utility.querySelector('.menu-drawer__localization');
      const row = document.createElement('div');
      row.className = 'cb-accessibility-trigger-row';
      utility.insertBefore(row, localization || utility.firstChild);
      if (localization) row.appendChild(localization);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cb-accessibility-trigger-button';
      button.setAttribute('aria-label', 'Open accessibility widget');
      button.setAttribute('title', 'Accessibility settings');
      button.setAttribute('onclick', 'window.accessEzWidget.toggle()');
      button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle class="cb-accessibility-icon-ring" cx="12" cy="12" r="10"></circle><circle cx="12" cy="6.8" r="1.5"></circle><path d="M5.5 10.2h13M12 10.2v5.1m0 0-3.3 4.1M12 15.3l3.3 4.1"></path></svg>';
      row.appendChild(button);

      const link = document.createElement('a');
      link.className = 'cb-accessibility-trigger-link';
      link.href = '#open-accessease-widget';
      link.textContent = 'Accessibility';
      row.appendChild(link);
    });
  };

  const startAccessibilityTriggers = () => {
    installAccessibilityTriggers();
    new MutationObserver(installAccessibilityTriggers).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startAccessibilityTriggers, { once: true });
  } else {
    startAccessibilityTriggers();
  }

  const accessibilityStyles = document.createElement('style');
  accessibilityStyles.textContent = `
    .cb-accessibility-trigger-row {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-wrap: wrap;
      gap: 8px;
      width: 100%;
    }
    .cb-accessibility-trigger-row .menu-drawer__localization {
      flex: 0 1 auto;
      min-width: 0;
      margin-inline-end: 4px;
    }
    .cb-accessibility-trigger-button {
      appearance: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 44px;
      padding: 7px;
      border: 0;
      background: transparent;
      color: inherit;
      cursor: pointer;
    }
    .cb-accessibility-trigger-button svg {
      width: 23px;
      height: 23px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.6;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .cb-accessibility-trigger-button .cb-accessibility-icon-ring {
      stroke-width: 1.5;
    }
    .cb-accessibility-trigger-link {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      color: inherit;
      font: inherit;
      font-size: 14px;
      text-decoration: none;
    }
    .cb-accessibility-trigger-button:focus-visible,
    .cb-accessibility-trigger-link:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 3px;
    }
    /* MOBILE NAV DRAWER SPACING + PRODUCT CARD TRUNCATION */
    .menu-drawer__menu { padding-inline: 8px !important; }
    .menu-drawer__menu-item--mainlist {
      min-height: 0 !important;
      padding-block: 8px !important;
      padding-inline: 0 !important;
      line-height: 1.2 !important;
    }
    .menu-drawer__menu.has-submenu { margin-block-end: 10px !important; }
    .menu-drawer__utility-links { padding-inline: 8px !important; }
    .cb-accessibility-trigger-row { gap: 8px !important; margin-inline-start: 0 !important; }
    .menu-drawer__featured-content-list {
      gap: 10px !important;
      padding-inline: 0 !important;
      padding-block-end: 16px !important;
    }
    .menu-drawer__featured-content-list-item:first-child { margin-inline-start: 8px !important; }
    .menu-drawer__featured-content-list-item:last-child { margin-inline-end: 8px !important; }
    .menu-drawer__featured-content-list-item--product { width: 34cqi !important; }
    .menu-drawer .resource-card__content { gap: 4px !important; min-width: 0; }
    .menu-drawer .resource-card__title {
      display: block !important;
      width: 100%;
      max-width: 100%;
      overflow: hidden !important;
      white-space: nowrap !important;
      text-overflow: ellipsis !important;
      font-size: 11px !important;
      line-height: 1.25 !important;
      font-weight: 400 !important;
      margin-block: 0 !important;
    }
    .menu-drawer .resource-card__content .price,
    .menu-drawer .resource-card__content .price *,
    .menu-drawer .resource-card__content product-price,
    .menu-drawer .resource-card__content product-price * {
      font-size: 10px !important;
      line-height: 1.25 !important;
      font-weight: 400 !important;
      letter-spacing: .01em !important;
      white-space: nowrap !important;
    }

  `;
  document.head.appendChild(accessibilityStyles);
})();
